import React from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useOrg } from "../context/OrgContext";
import { useTerminology } from "../context/TerminologyContext";
import RoleSwitcher from "./RoleSwitcher";
import OrgSwitcher from "./OrgSwitcher";
import AppIcon from "./AppIcon";
import { formatDateTime } from "../utils/datetime";
import useAutoRefresh from "../hooks/useAutoRefresh";

function initials(value) {
  const raw = String(value || "").trim();
  if (!raw) return "U";
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

export default function Topbar() {
  const { user, logout, visibleRoles } = useAuth();
  const { orgs, current: currentOrg } = useOrg();
  const { t } = useTerminology();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [identityOpen, setIdentityOpen] = React.useState(false);
  const [unread, setUnread] = React.useState(0);
  const [items, setItems] = React.useState([]);
  const notifRef = React.useRef(null);
  const identityRef = React.useRef(null);

  const loadInbox = React.useCallback(async () => {
    if (!user?.uid) return;
    const response = await api.get("/notifications/inbox", {
      params: { uid: user.uid, unread_only: true },
    });
    const arr = Array.isArray(response.data?.items) ? response.data.items : [];
    setItems(arr);
    setUnread(arr.length);
  }, [user?.uid]);

  React.useEffect(() => {
    if (!user?.uid) {
      setItems([]);
      setUnread(0);
      return undefined;
    }
    loadInbox().catch(() => {
      setItems([]);
      setUnread(0);
    });
    return undefined;
  }, [loadInbox, user?.uid]);

  useAutoRefresh(loadInbox, { enabled: !!user?.uid, intervalMs: 20000 });

  React.useEffect(() => {
    const handlePointerDown = (event) => {
      if (
        notifOpen &&
        notifRef.current &&
        !notifRef.current.contains(event.target)
      )
        setNotifOpen(false);
      if (
        identityOpen &&
        identityRef.current &&
        !identityRef.current.contains(event.target)
      )
        setIdentityOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setNotifOpen(false);
        setIdentityOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [notifOpen, identityOpen]);

  const hasRoleSwitcher = (visibleRoles?.(user) || []).length > 1;
  const hasOrgSwitcher =
    (user?.currentRole === "Super Admin" || user?.currentRole === "Vendor") &&
    Array.isArray(orgs) &&
    orgs.length > 1;

  const markRead = React.useCallback(
    async (ids) => {
      const targetIds = Array.isArray(ids) ? ids.filter(Boolean) : [];
      if (!user?.uid || !targetIds.length) return false;
      const form = new FormData();
      form.append("uid", user.uid);
      form.append("ids", targetIds.join(","));
      await api.post("/notifications/mark_read", form);
      return true;
    },
    [user?.uid],
  );

  const clearNotifications = React.useCallback(
    async (ids) => {
      const targetIds =
        Array.isArray(ids) && ids.length
          ? ids.filter(Boolean)
          : items.map((item) => item.id).filter(Boolean);
      if (!targetIds.length) return;
      try {
        await markRead(targetIds);
        setItems((prev) => prev.filter((row) => !targetIds.includes(row.id)));
        setUnread((prev) => Math.max(0, prev - targetIds.length));
      } catch {
        loadInbox().catch(() => {});
      }
    },
    [items, loadInbox, markRead],
  );

  const openItem = async (item) => {
    try {
      await markRead([item.id]);
    } catch {
      // no-op
    }
    setItems((prev) => prev.filter((row) => row.id !== item.id));
    setUnread((prev) => Math.max(0, prev - 1));
    setNotifOpen(false);
    navigate(item.link || "/notifications");
  };

  const roleLabel = React.useCallback(
    (role) => (role === "Vendor" ? t("vendor") : role),
    [t],
  );
  const currentRoleLabel = roleLabel(user?.currentRole) || "Workspace";
  const workspaceLabel =
    user?.currentRole === "Super Admin" ? "Organization" : "Workspace";
  const primaryIdentity = user?.email || user?.uid || "User";
  const visibleItems = items.slice(0, 10);

  return (
    <div className="topbar premium-fade-in">
      <div className="left">
        <div className="chip chip-role">
          <span>{currentRoleLabel}</span>
        </div>
        <img
          className="topbar-client-logo"
          src={`${process.env.PUBLIC_URL || ""}/brand/client.png`}
          alt="Client logo"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      </div>

      <div className="right topbar-actions">
        <div className="notif-wrap" ref={notifRef}>
          <button
            className="icon-btn icon-btn--circle icon-btn--glow"
            type="button"
            title="Notifications"
            aria-label="Notifications"
            aria-expanded={notifOpen}
            onClick={() => {
              setNotifOpen((value) => {
                const next = !value;
                if (next) {
                  setIdentityOpen(false);
                  loadInbox().catch(() => {});
                }
                return next;
              });
            }}
          >
            <AppIcon name="bell" />
            {unread > 0 ? <span className="badge">{unread}</span> : null}
          </button>

          {notifOpen ? (
            <div
              className="notif-dropdown premium-popover"
              role="dialog"
              aria-label="Notifications"
            >
              <div className="notif-dropdown-header">
                <div>
                  <strong>Notifications</strong>
                  <div className="muted-copy">
                    Latest unread workflow activity
                  </div>
                </div>
                <div className="notif-dropdown-actions">
                  {items.length > 0 ? (
                    <button
                      className="notif-text-btn"
                      type="button"
                      onClick={() => clearNotifications([])}
                    >
                      Clear all
                    </button>
                  ) : null}
                  <button
                    className="notif-text-btn"
                    type="button"
                    onClick={() => {
                      setNotifOpen(false);
                      navigate("/notifications");
                    }}
                  >
                    View all
                  </button>
                </div>
              </div>
              <div className="notif-list notif-list--dropdown">
                {visibleItems.length === 0 ? (
                  <div className="notif-empty muted-copy">
                    No new notifications.
                  </div>
                ) : (
                  visibleItems.map((item) => (
                    <div key={item.id} className="notif-row">
                      <button
                        className="notif-item"
                        type="button"
                        onClick={() => openItem(item)}
                      >
                        <div className="notif-row-copy">
                          <div className="notif-title">{item.title}</div>
                          <div className="notif-message">{item.message}</div>
                          <div className="notif-meta">
                            {formatDateTime(item.time)}
                          </div>
                        </div>
                      </button>
                      <button
                        className="icon-btn icon-btn--circle notif-clear-btn"
                        type="button"
                        aria-label={`Clear notification ${item.title}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          clearNotifications([item.id]);
                        }}
                      >
                        <AppIcon name="close" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div className="topbar-identity-wrap" ref={identityRef}>
          <button
            className="topbar-identity-btn"
            type="button"
            aria-label="Open user details"
            aria-expanded={identityOpen}
            onClick={() => {
              setIdentityOpen((value) => {
                const next = !value;
                if (next) setNotifOpen(false);
                return next;
              });
            }}
          >
            <span className="topbar-identity-icon">
              <span className="topbar-identity-initials">
                {initials(primaryIdentity)}
              </span>
            </span>
          </button>
          {identityOpen ? (
            <div
              className="identity-popover premium-popover"
              role="dialog"
              aria-label="User details"
            >
              <div className="identity-popover-header">
                <div className="topbar-identity-icon topbar-identity-icon--panel">
                  <span className="topbar-identity-initials">
                    {initials(primaryIdentity)}
                  </span>
                </div>
                <div>
                  <div className="identity-title">{user?.uid || "User"}</div>
                  <div className="identity-subtitle">{currentRoleLabel}</div>
                </div>
              </div>
              <div className="identity-details">
                <div className="identity-detail-row">
                  <span>{workspaceLabel}</span>
                  <strong>{currentOrg || "default"}</strong>
                </div>
                <div className="identity-detail-row">
                  <span>User</span>
                  <strong>{user?.uid || "—"}</strong>
                </div>
              </div>
              <div className="identity-actions">
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setIdentityOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {hasOrgSwitcher || hasRoleSwitcher ? (
          <div className="topbar-role-switcher-wrap">
            <OrgSwitcher />
            <RoleSwitcher />
          </div>
        ) : null}

        <button
          className="icon-btn icon-btn--circle topbar-logout-btn"
          type="button"
          onClick={logout}
          title="Logout"
          aria-label="Logout"
        >
          <AppIcon name="logout" />
        </button>
      </div>
    </div>
  );
}
